import {useCallback} from 'react'

import {RiShare2Line, RiShareLine} from '@remixicon/react'
import {toast} from 'react-toastify'

import {
  MainButton,
  MainIconButton,
  OutlinedButton,
} from '@festi/common/components/buttons'
import {styledTheme} from '@festi/common/themes'

interface ShareButtonProps {
  btnLabel: string
  shareTitle: string
  sharePath: string
  errorMsg: string
  clipboardSuccessMsg: string
  clipboardErrorMsg: string
  iconOnly?: boolean
  newDesign: boolean
}

export default function ShareButton({
  btnLabel,
  shareTitle,
  sharePath,
  errorMsg,
  clipboardSuccessMsg,
  clipboardErrorMsg,
  iconOnly = false,
  newDesign,
}: ShareButtonProps) {
  const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator?.userAgent)

  const handleClick = useCallback(() => {
    if (typeof navigator.share !== 'undefined') {
      navigator
        .share({
          title: shareTitle,
          text: '',
          url: `${window.location.protocol}//${window.location.host}${sharePath}`,
        })
        .then(() => console.log('Share was successful.'))
        .catch((error) => {
          if (
            error.name === 'AbortError' ||
            error.message === 'Share cancelled' ||
            error.message === 'The user aborted a request.'
          ) {
            console.log('Share was cancelled by the user.')
          } else {
            toast.error(errorMsg)
          }
        })
    } else {
      navigator.clipboard
        .writeText(
          `${window.location.protocol}//${window.location.host}${sharePath}`,
        )
        .then(() => {
          toast.success(clipboardSuccessMsg)
        })
        .catch((_e) => {
          toast.error(clipboardErrorMsg)
        })
    }
  }, [sharePath, shareTitle, errorMsg, clipboardSuccessMsg, clipboardErrorMsg])

  // Note: This button is used in comparison page were we don't have the new design
  // yet hence the newDesign prop and old outlined button
  return (
    <>
      {newDesign ? (
        iconOnly ? (
          <MainIconButton
            buttonVariant="ghost"
            icon={isMac ? RiShare2Line : RiShareLine}
            onClick={handleClick}
            iconColor={styledTheme.palette.ui20Solid}
          />
        ) : (
          <MainButton
            buttonVariant="ghost"
            label={btnLabel}
            suffixIcon={isMac ? RiShare2Line : RiShareLine}
            iconColor={styledTheme.palette.ui20Solid}
            onClick={handleClick}
            fullWidth
          />
        )
      ) : (
        <OutlinedButton
          icon={isMac ? RiShare2Line : RiShareLine}
          onClick={handleClick}
        >
          {btnLabel}
        </OutlinedButton>
      )}
    </>
  )
}
